import { AlmanacFile, AlmanacFileSummary, KanbanView, KanbanViewColumn, Permission } from 'types'
import { sendToApi } from 'api/client'

export interface MinimalProject {
  admin: boolean
  id: number
  title: string
  workspace_id: number
  current_user_access: string
  key: string
}

export interface Project {
  default_project?: boolean
  id: number
  title: string
  workspace_id: number
  key: string
  has_edit_access: boolean
  url: string
  handbook_mode?: boolean
  database_mode?: boolean
  root_handbook_seo_string?: string | null
  root_handbook_key?: string | null
  workflow_id: string | null
  almanac_file: AlmanacFileSummary
}

export enum ProjectMembershipLevel {
  Admin = 'admin',
  Member = 'member',
}

export enum ProjectMemberType {
  Team = 'Channel',
  User = 'User',
}

export interface ProjectMember {
  avatar_url?: string | null
  emoji: string | null
  id: number
  name: string
  type: ProjectMemberType
}

export interface ProjectMembership {
  id: number
  level: ProjectMembershipLevel
  member: ProjectMember
  permission: Permission
}

export const ProjectClient = {
  deleteProject: (projectId: number) => sendToApi<void>(`/projects/${projectId}`, { method: 'DELETE' }),
  deleteProjectMembership: (projectMembershipId: number) =>
    sendToApi<void>(`/project_memberships/${projectMembershipId}`, { method: 'DELETE' }),
  updateProjectMembership: (
    projectMembershipId: number,
    projectMembership: Partial<Pick<ProjectMembership, 'level' | 'permission'>>,
  ) =>
    sendToApi<{ project_membership: ProjectMembership }>(`/project_memberships/${projectMembershipId}`, {
      method: 'PUT',
      body: { project_membership: projectMembership },
    }),
  createProjectMemberships: (
    projectId: string,
    permission: string,
    members: { id?: number; email?: string; type: string }[],
  ) =>
    sendToApi<{ project_membership: ProjectMembership }>(`/projects/${projectId}/project_memberships`, {
      method: 'POST',
      body: {
        project_memberships: {
          permission: permission,
          members: members,
        },
      },
    }),
  transferOwnership: (key: string, user_id: number) =>
    sendToApi<{}>(`/projects/${key}/transfers`, {
      method: 'POST',
      body: {
        user_id,
      },
    }),
  getKanbanView: (projectId: string, kanbanViewId: number) =>
    sendToApi<{ kanban_view: KanbanView }>(`/projects/${projectId}/kanban_views/${kanbanViewId}`),
  updateKanbanView: (projectId: string, kanbanViewId: number, columns: KanbanViewColumn[]) =>
    sendToApi<{ kanban_view: KanbanView }>(`/projects/${projectId}/kanban_views/${kanbanViewId}`, {
      method: 'PUT',
      body: { columns },
    }),
  unscheduledArticles: (projectId: string) => {
    return ({ pageParam = 1 }) => {
      return sendToApi<{ articles: AlmanacFile[] }>(`/projects/${projectId}/articles/unscheduled`, {
        query: { page: pageParam },
      })
    }
  },
}
